/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 블록 전체에 포커스가 있는 것처럼 꾸미는 캐럿정지점을 정의한다.
 *
 * @file /src/rootin/elements/anchor/Anchor.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 28.
 */
import * as parent from '@rootin/base/Element';
import { Block } from '@rootin/base/Block';
import './Anchor.css';

export interface Properties extends parent.Properties {}

export class Anchor extends parent.Element {
    /**
     * 엘리먼트타입
     */
    type: string = 'anchor';

    /**
     * 엘리먼트종류
     */
    kind: 'text' | 'atomic' | 'container' = 'text';

    /**
     * 블록의 유일한 캐럿정지점 역할을 가질지 여부
     */
    alwaysEditable: boolean = false;

    /**
     * 블록 전체에 포커스가 있는 것처럼 꾸미는 캐럿정지점을 정의한다.
     *
     * @param block - 이 엘리먼트가 속한 블록
     * @param properties - 엘리먼트설정
     */
    constructor(
        block: Block,
        public properties: Properties
    ) {
        super(block, properties);

        this.$getElement().setAttr('data-role', 'anchor');
        this.$getElement().html('<br>');
        this.setEditable(false);

        this.$getElement().on('keydown', (_, e) => {
            if (e.key === 'Backspace' || e.key === 'Delete') {
                const container = this.block.getParent();
                if (container === null) {
                    return;
                }

                container.removeBlock(this.block);
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            const DIRECTIONS: { [key: string]: 'up' | 'down' | 'left' | 'right' } = {
                ArrowUp: 'up',
                ArrowDown: 'down',
                ArrowLeft: 'left',
                ArrowRight: 'right',
            };

            const direction = e.key === 'Tab' ? (e.shiftKey === true ? 'backward' : 'forward') : DIRECTIONS[e.key];
            if (direction === undefined) {
                return;
            }

            if (this.block.onCaretMove(direction) === true) {
                e.preventDefault();
                e.stopPropagation();
            }
        });
    }

    /**
     * 캐럿정지점으로서의 활성여부를 지정한다.
     *
     * @param editable - 캐럿정지점으로 쓸지 여부
     */
    setEditable(editable: boolean): void {
        this.$getElement().setAttr('contenteditable', editable === true ? 'true' : 'false');
        this.$getElement().setAttr('data-editable', editable === true ? 'true' : 'false');
    }

    /**
     * 이 앵커가 블록의 유일한 캐럿정지점 역할까지 겸하도록 설정한다.
     *
     * @param isAlways - 항상 활성화 여부
     */
    setAnchorAlways(isAlways: boolean): void {
        this.alwaysEditable = isAlways;

        if (isAlways === false && document.activeElement === this.$getElement().getEl()) {
            return;
        }

        this.setEditable(isAlways);
    }

    /**
     * 현재 캐럿 오프셋을 가져온다.
     *
     * @returns 캐럿 오프셋
     */
    captureCaret(): any {
        return {};
    }

    /**
     * 엘리먼트에 포커스를 지정한다.
     *
     * @param to_start - 포커스 위치 (TRUE : 처음, FALSE : 끝, NULL : 앵커)
     */
    focus(to_start: boolean = false): void {
        this.setEditable(true);
        super.focus(to_start);
    }

    /**
     * 엘리먼트를 렌더링한다. 이 엘리먼트는 생성자에서 이미 모든 걸 끝내두므로 할 일이 없다.
     */
    async render(): Promise<void> {}

    /**
     * 엘리먼트가 비었는지 확인한다.
     *
     * @returns 항상 true
     */
    isEmpty(): boolean {
        return true;
    }

    /**
     * 엘리먼트값을 가져온다.
     *
     * @returns 기본값 null
     */
    getValue(): any {
        return null;
    }

    /**
     * 엘리먼트값을 지정한다.
     *
     * @param value - (무시됨)
     */
    async setValue(value: any): Promise<void> {}

    /**
     * 엘리먼트가 포커스를 잃었을 때 이벤트를 처리한다.
     */
    onBlur(): void {
        if (this.alwaysEditable === false) {
            this.setEditable(false);
        }
    }
}
